import { Box, Grid } from "@mui/material";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import { AcxExpandableText } from "components/UI/AcxExpandableText";
import { Duration } from "components/UI/Duration";
import { JSONViewer } from "components/UI/JSONViewer";
import Markdown from "markdown-to-jsx";
import { LLMExecutorExecutionResult } from "models/LLMs/Testing/LLMExecutorExecutionResult";
import React from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { SchemaBasedJSONViewer } from "components/UI/SchemaBasedJSONViewer";

const ReasoningKey = "reasoning";
const StructuredOutputKey = "structured_output";

const LLMExecutorExecutionResultContext =
    React.createContext<Partial<LLMExecutorExecutionResult> | null>(null);
function useLLMExecutorExecutionResult() {
    const context = React.useContext(LLMExecutorExecutionResultContext);
    if (!context) {
        throw new Error(
            "useLLMExecutorExecutionResult must be used within a LLMExecutorExecutionResultContext",
        );
    }
    return context;
}

interface LLMExecutionResultInformationProps {
    executionResult: Partial<LLMExecutorExecutionResult>;
    children: React.ReactNode;
}

export function LLMExecutionResultProvider(props: {
    executionResult: Partial<LLMExecutorExecutionResult>;
    children: React.ReactNode;
}) {
    return (
        <LLMExecutorExecutionResultContext.Provider
            value={props.executionResult}
        >
            {props.children}
        </LLMExecutorExecutionResultContext.Provider>
    );
}

export function LLMExecutionResultInformation({
    executionResult,
    children,
}: LLMExecutionResultInformationProps) {
    return (
        <LLMExecutionResultProvider executionResult={executionResult}>
            <TitledSection
                title={
                    <Grid
                        container
                        component="span"
                        alignItems="center"
                        columnGap={1}
                        fontSize="13px"
                    >
                        <Grid item>Detailed information</Grid>
                    </Grid>
                }
                collapsible
                defaultCollapsed={true}
                variant="bordered"
            >
                <Grid container direction="column" rowGap={2}>
                    {children}
                </Grid>
            </TitledSection>
        </LLMExecutionResultProvider>
    );
}

function StepRunInformation() {
    const getStepName = useGetStepName();
    const { stepRunInformation } = useLLMExecutorExecutionResult();

    if (!stepRunInformation) {
        console.error("No step run information");
        return null;
    }

    return (
        <Box sx={{ overflowX: "auto" }}>
            <TitledSection
                title="Step Run Information"
                collapsible
                variant="bordered"
            >
                <Grid
                    container
                    direction="column"
                    spacing={1}
                    sx={{ width: "100%" }}
                    p={1}
                >
                    <Grid
                        container
                        item
                        spacing={1}
                        sx={{
                            fontWeight: "bold",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                            pb: 1,
                        }}
                    >
                        <Grid item xs={1.5}>
                            Step
                        </Grid>
                        <Grid item xs={1.5}>
                            Triggered By
                        </Grid>
                        <Grid item xs={1.5}>
                            Duration (seconds)
                        </Grid>
                        <Grid item xs={1.5}>
                            Message
                        </Grid>
                        <Grid item xs={3}>
                            Reasoning
                        </Grid>
                        <Grid item xs={3}>
                            Output
                        </Grid>
                    </Grid>

                    {stepRunInformation.map((step, idx) => (
                        <Grid
                            container
                            item
                            spacing={1}
                            key={step.stepInstanceId}
                            sx={{
                                borderBottom:
                                    idx < stepRunInformation.length - 1
                                        ? "1px solid rgba(0, 0, 0, 0.06)"
                                        : "none",
                                py: 1,
                            }}
                        >
                            <Grid item xs={1.5}>
                                {getStepName(step.stepId)}
                            </Grid>
                            <Grid item xs={1.5}>
                                {getStepName(step.triggeredByStepId ?? "") ||
                                    "-"}
                            </Grid>
                            <Grid item xs={1.5}>
                                <Duration seconds={step.durationInSeconds} />
                            </Grid>
                            <Grid item xs={1.5}>
                                {step.message}
                            </Grid>
                            <Grid item xs={3}>
                                <AcxExpandableText>
                                    <Markdown>
                                        {getReasoning(step.outputVariables)}
                                    </Markdown>
                                </AcxExpandableText>
                            </Grid>
                            <Grid item xs={3}>
                                <AcxExpandableText>
                                    <JSONViewer
                                        value={getAllOutputVariablesExceptReasoning(
                                            step.outputVariables,
                                        )}
                                    />
                                </AcxExpandableText>
                            </Grid>
                        </Grid>
                    ))}
                </Grid>
            </TitledSection>
        </Box>
    );
}

function ClassifierResultsToWrite() {
    const { classifierResultsToWrite } = useLLMExecutorExecutionResult();
    const getStepName = useGetStepName();

    const classifierResultsToWriteEntries = Object.entries(
        classifierResultsToWrite ?? {},
    ).sort((a, b) => a[0].localeCompare(b[0]));

    return (
        <TitledSection
            title="Classifier Results to Write"
            collapsible
            defaultCollapsed
            variant="bordered"
        >
            <Grid
                container
                direction="column"
                spacing={1}
                sx={{ width: "100%" }}
                p={1}
            >
                <Grid
                    container
                    item
                    spacing={1}
                    sx={{
                        fontWeight: "bold",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        pb: 1,
                    }}
                >
                    <Grid item xs={4}>
                        Classifier ID
                    </Grid>
                    <Grid item xs={2}>
                        From Step
                    </Grid>
                    <Grid item xs={2}>
                        Value to Write
                    </Grid>
                    <Grid item xs={4}>
                        Reasoning
                    </Grid>
                </Grid>

                {classifierResultsToWriteEntries.map(
                    ([classifierId, value], idx) => (
                        <Grid
                            container
                            item
                            spacing={1}
                            key={classifierId}
                            sx={{
                                borderBottom:
                                    idx <
                                    classifierResultsToWriteEntries.length - 1
                                        ? "1px solid rgba(0, 0, 0, 0.06)"
                                        : "none",
                                py: 1,
                            }}
                        >
                            <Grid item xs={4}>
                                <Box
                                    component="pre"
                                    sx={{
                                        fontFamily: "monospace",
                                        m: 0,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {classifierId}
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                {getStepName(value.writtenFromStepId)}
                            </Grid>
                            <Grid item xs={2}>
                                {value.decision}
                            </Grid>
                            <Grid item xs={4}>
                                <AcxExpandableText>
                                    <Markdown>{value.reasoning ?? ""}</Markdown>
                                </AcxExpandableText>
                            </Grid>
                        </Grid>
                    ),
                )}
            </Grid>
        </TitledSection>
    );
}

function ExtendedMetadataToWrite() {
    const { extendedMetadataToWrite } = useLLMExecutorExecutionResult();
    const getStepName = useGetStepName();
    const store = useStore(LLMClassifierBuilderStore);

    const extendedMetadataToWriteEntries = Object.entries(
        extendedMetadataToWrite ?? {},
    ).sort((a, b) => a[0].localeCompare(b[0]));

    return (
        <TitledSection
            title="Extended Metadata to Write"
            collapsible
            variant="bordered"
        >
            <Grid
                container
                direction="column"
                spacing={1}
                sx={{ width: "100%" }}
                p={1}
            >
                <Grid
                    container
                    item
                    spacing={1}
                    sx={{
                        fontWeight: "bold",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        pb: 1,
                    }}
                >
                    <Grid item xs={2}>
                        Extended Metadata Field Id
                    </Grid>
                    <Grid item xs={2}>
                        Field Name
                    </Grid>
                    <Grid item xs={2}>
                        From Step
                    </Grid>
                    <Grid item xs={6}>
                        Value to Write
                    </Grid>
                </Grid>

                {extendedMetadataToWriteEntries.map(
                    ([extendedMetadataClassifierId, value], idx) => {
                        const extendedMetadataClassifier =
                            store.allExtendedMetadata.find(
                                (c) => c.id === extendedMetadataClassifierId,
                            );

                        return (
                            <Grid
                                container
                                item
                                spacing={1}
                                key={extendedMetadataClassifierId}
                                sx={{
                                    borderBottom:
                                        idx <
                                        extendedMetadataToWriteEntries.length -
                                            1
                                            ? "1px solid rgba(0, 0, 0, 0.06)"
                                            : "none",
                                    py: 1,
                                }}
                            >
                                <Grid item xs={2}>
                                    {extendedMetadataClassifierId}
                                </Grid>
                                <Grid item xs={2}>
                                    {extendedMetadataClassifier?.name}
                                </Grid>
                                <Grid item xs={2}>
                                    {getStepName(value.writtenFromStepId)}
                                </Grid>
                                <Grid item xs={6}>
                                    {!!extendedMetadataClassifier?.jsonSchema ? (
                                        <SchemaBasedJSONViewer
                                            value={value.value}
                                            schema={
                                                extendedMetadataClassifier.jsonSchema
                                            }
                                        />
                                    ) : (
                                        <JSONViewer value={value.value} />
                                    )}
                                </Grid>
                            </Grid>
                        );
                    },
                )}
            </Grid>
        </TitledSection>
    );
}

function StepVariables() {
    const { stepVariables } = useLLMExecutorExecutionResult();

    const stepVariablesEntries = Object.entries(stepVariables || {}).sort(
        (a, b) => a[0].localeCompare(b[0]),
    );

    if (stepVariablesEntries.length === 0) {
        return null;
    }

    return (
        <TitledSection title="All Variables" collapsible variant="bordered">
            <Grid
                container
                direction="column"
                spacing={1}
                sx={{ width: "100%" }}
                p={1}
            >
                <Grid
                    container
                    item
                    spacing={1}
                    sx={{
                        fontWeight: "bold",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        pb: 1,
                    }}
                >
                    <Grid item xs={4}>
                        Variable
                    </Grid>
                    <Grid item xs={8}>
                        Value
                    </Grid>
                </Grid>

                {stepVariablesEntries.map(([key, value], idx) => (
                    <Grid
                        container
                        item
                        spacing={1}
                        key={key}
                        sx={{
                            borderBottom:
                                idx < stepVariablesEntries.length - 1
                                    ? "1px solid rgba(0, 0, 0, 0.06)"
                                    : "none",
                            py: 1,
                        }}
                    >
                        <Grid item xs={4} sx={{ minWidth: 0 }}>
                            <Box
                                component="pre"
                                sx={{
                                    fontFamily: "monospace",
                                    m: 0,
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {key}
                            </Box>
                        </Grid>
                        <Grid item xs={8} sx={{ minWidth: 0 }}>
                            <AcxExpandableText>
                                {key.includes(ReasoningKey) ? (
                                    <Markdown>{String(value)}</Markdown>
                                ) : key.includes(StructuredOutputKey) ? (
                                    <Markdown>
                                        {`\`\`\`${JSON.stringify(value)}\`\`\``}
                                    </Markdown>
                                ) : (
                                    <Box
                                        sx={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            maxWidth: "500px",
                                        }}
                                    >
                                        {String(value)}
                                    </Box>
                                )}
                            </AcxExpandableText>
                        </Grid>
                    </Grid>
                ))}
            </Grid>
        </TitledSection>
    );
}

LLMExecutionResultInformation.StepRunInformation = StepRunInformation;
LLMExecutionResultInformation.ClassifierResultsToWrite =
    ClassifierResultsToWrite;
LLMExecutionResultInformation.ExtendedMetadataToWrite = ExtendedMetadataToWrite;
LLMExecutionResultInformation.StepVariables = StepVariables;

function useGetStepName() {
    const store = useStore(LLMClassifierBuilderStore);
    const steps = store.classifier.implementation.isLogicChain()
        ? store.classifier.implementation.logicChainBuilder.steps
        : [];

    function getStepName(stepId: string) {
        const step = steps.find((s) => s.id === stepId);
        if (!step) {
            return "(entry point)";
        }

        return step.name;
    }

    return getStepName;
}

function getReasoning(outputVariables: Record<string, string>) {
    if (outputVariables[ReasoningKey]) {
        return outputVariables[ReasoningKey];
    }
    return "";
}

function getAllOutputVariablesExceptReasoning(
    outputVariables: Record<string, string>,
) {
    const { [ReasoningKey]: _, ...rest } = outputVariables;
    return rest;
}
